import { useTranslation } from "react-i18next";

export function scoreColor(score: number): string {
  if (score >= 70) return "hsl(var(--primary))";
  if (score >= 40) return "hsl(var(--warning))";
  return "hsl(var(--muted-foreground))";
}

function scoreTierKey(score: number): string {
  if (score >= 70) return "card.tierHigh";
  if (score >= 40) return "card.tierMid";
  return "card.tierLow";
}

interface MiniScoreDialProps {
  score: number | null;
  size?: number;
}

export function MiniScoreDial({ score, size = 44 }: MiniScoreDialProps) {
  const { t } = useTranslation("results");

  if (score == null || !Number.isFinite(score)) return null;

  const clamped = Math.max(0, Math.min(100, score));
  const rounded = Math.round(clamped);
  const strokeWidth = 4;
  const r = (size - strokeWidth * 2) / 2;
  const cx = size / 2;
  const cy = size / 2;
  const circumference = 2 * Math.PI * r;
  const color = scoreColor(clamped);
  const tierLabel = t(scoreTierKey(clamped));

  return (
    <div className="shrink-0 flex flex-col items-center gap-0.5">
      <div
        role="img"
        aria-label={t("card.scoreAria", { score: rounded, label: tierLabel })}
      >
        <svg
          width={size}
          height={size}
          viewBox={`0 0 ${size} ${size}`}
          aria-hidden="true"
        >
          <circle
            cx={cx}
            cy={cy}
            r={r}
            strokeWidth={strokeWidth}
            stroke="hsl(var(--muted))"
            fill="none"
          />
          <circle
            data-testid="score-arc"
            cx={cx}
            cy={cy}
            r={r}
            strokeWidth={strokeWidth}
            stroke={color}
            fill="none"
            strokeDasharray={circumference}
            strokeDashoffset={circumference * (1 - clamped / 100)}
            strokeLinecap="round"
            transform={`rotate(-90 ${cx} ${cy})`}
          />
          <text
            x={cx}
            y={cy}
            dominantBaseline="middle"
            textAnchor="middle"
            fontSize="11"
            fontWeight="700"
            fill="currentColor"
          >
            {rounded}
          </text>
        </svg>
      </div>
      <span
        aria-hidden="true"
        style={{ fontSize: "9.5px" }}
        className="uppercase text-muted-foreground font-mono tracking-wide leading-none"
      >
        {tierLabel}
      </span>
    </div>
  );
}
